import { Link } from 'react-router-dom'
import { useDocsIndex } from '../lib/docsIndex'
import { useAuth } from '../lib/auth'
import { signInWithSightHub } from '../lib/sso'
import { flattenTree } from '../lib/types'
import { useMeta } from '../lib/seo'
import { ExportDocs } from '../components/ExportDocs'
import { Chip, EmptyState, Spinner, VisibilityBadge } from '../components/ui'

export function HomePage() {
  const { tree, loading, pathById } = useDocsIndex()
  const { user, staff, loading: authLoading, signIn } = useAuth()

  useMeta({
    title: 'SightHub Wiki',
    description:
      'Reference documentation for every SightHub service: APIs, routes, releases and optometry terminology.',
    path: '/',
  })

  const docCount = flattenTree(tree).length

  return (
    <div className="mx-auto max-w-4xl px-1 py-16 lg:px-8">
      <img
        src="/sighthub-logo.png"
        alt=""
        width={56}
        height={56}
        className="mb-5 size-14 object-contain"
      />
      <h1 className="text-4xl font-semibold tracking-tight text-ink">SightHub Wiki</h1>
      <p className="mt-3 max-w-xl text-[17px] text-muted">
        Reference documentation for every SightHub service. Press{' '}
        <kbd className="rounded border border-line bg-sunken px-1.5 py-0.5 font-mono text-xs">/</kbd>{' '}
        to search.
      </p>

      {!user && (
        <p className="mt-6 text-sm text-subtle">
          <button onClick={() => void signIn()} className="font-medium text-accent-text hover:underline">
            Sign in
          </button>{' '}
          to comment, vote, and save pages. Staff sign in with SightHub to see internal docs.
        </p>
      )}
      {/* Not while the role is still resolving, or staff would see this flash past. */}
      {user && !staff && !authLoading && (
        <p className="mt-6 text-sm text-subtle">
          You are signed in without a staff role, so internal docs stay hidden. Staff:{' '}
          <button onClick={signInWithSightHub} className="font-medium text-accent-text hover:underline">
            sign in with SightHub
          </button>{' '}
          instead.
        </p>
      )}

      {loading ? (
        <div className="mt-14">
          <Spinner label="Loading services…" />
        </div>
      ) : docCount === 0 ? (
        <div className="mt-14">
          <EmptyState title="Nothing published yet">
            Docs appear here once the backend publishes them.
          </EmptyState>
        </div>
      ) : (
        <div className="mt-14 grid gap-4 sm:grid-cols-2">
          {tree.map((service) => {
            const docs = flattenTree([service])
            const tags = [...new Set(docs.flatMap((d) => d.tags))].slice(0, 4)
            const first = docs[0]

            return (
              <Link
                key={service.slug}
                to={first ? (pathById.get(first.id) ?? `/d/${first.id}`) : '/'}
                className="group rounded-[var(--radius-sh)] border border-line bg-surface p-5 shadow-card transition-shadow hover:shadow-pop"
              >
                <div className="flex items-center justify-between gap-2">
                  <h2 className="font-semibold text-ink group-hover:text-accent-text">
                    {service.title}
                  </h2>
                  <span className="font-mono text-xs text-subtle">{docs.length} docs</span>
                </div>

                <ul className="mt-3 space-y-1">
                  {docs.slice(0, 3).map((d) => (
                    <li key={d.id} className="flex items-center gap-2 truncate text-sm text-muted">
                      <span className="truncate">{d.title}</span>
                      <VisibilityBadge visibility={d.visibility} />
                    </li>
                  ))}
                </ul>

                {tags.length > 0 && (
                  <div className="mt-4 flex flex-wrap gap-1.5">
                    {tags.map((t) => (
                      <Chip key={t} label={t} />
                    ))}
                  </div>
                )}
              </Link>
            )
          })}
        </div>
      )}

      <div className="mt-12">
        <ExportDocs />
      </div>
    </div>
  )
}
